import { UnsAttributeType } from "../graphql/schema.js";
import { MeasurementUnit } from "./uns-measurements.js";
import { UnsTags } from "./uns-tags.js";
import { UnsTopics } from "./uns-topics.js";

export type ISO8601 = `${number}-${string}-${string}T${string}:${string}:${string}.${string}Z`;
export function isIOS8601Type(value: string): value is ISO8601 {
  const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  return iso8601Regex.test(value);
}
export type UnsAttribute = string;

export const valueTypes = ["string", "number"];
export type ValueTypeString = typeof valueTypes[number];
export type ValueType = string | number;

export interface IUnsParameters {
  mqttSubToTopics?: string | string[];
  username?: string;
  password?: string;
  mqttSSL?: boolean;
  publishThrottlingDelay?: number; // Delay in milliseconds; default is 1ms
  subscribeThrottlingDelay?: number; // Delay in milliseconds; default is 1ms
}

export interface IUnsProcessParameters {
  processName?: string | undefined;
  mqttSubToTopics?: string | string[];
  username?: string;
  password?: string;
  mqttSSL?: boolean;
}

export interface UnsEvents {
  // Emitters in MqttProxy, UnsMqttProxy
  input: { topic: string; message: string, packet: any };
  mqttProxyStatus: { event: string, value: number, uom: MeasurementUnit, statusTopic: string };
  
  // Emitters in MqttProxy
  error: { code: number; message: string };
  
  // Emitters in UnsMqttProxy
  mqttWorker: { command: string, instanceName: string; batchSize: number, referenceHash: string };

  // Emitter in UnsCronProxy
  cronEvent: {};

  // Emitters in UnsApiProxy
  apiGetEvent: {req: any, res: any};

  // Emitters in UnsProxy
  unsProxyProducedTopics: { producedTopics: ITopicObject[], statusTopic: string };

  // Emitters in UnsProxy
  unsProxyProducedApiEndpoints: { producedApiEndpoints: IApiObject[], statusTopic: string };
}

export interface IUnsExtendedData extends IUnsData {
  valueType: ValueTypeString;
}

export interface IUnsData {
  time: ISO8601;
  value: ValueType;
  dataGroup?: string;
  uom?: MeasurementUnit;
  foreignEventKey?: string;
}

export interface IUnsEvent {
  time: ISO8601;
  dataGroup?: string;
  details?: string;
  uniqueEventId?: string;
}

export interface IUnsTable {
  time: ISO8601;
  values: Record<string, string | number | undefined | null>
  dataGroup?: string;
}

export interface IUnsCommand {
  time: ISO8601;
  details?: string;
}

export interface IMqttMessage {
  topic: UnsTopics;
  attribute: UnsAttribute;
  description?: string;
  tags?: UnsTags[];
  packet: IUnsPacket;
  attributeNeedsPersistence?: boolean | null;
}

// This interface represents a packet for a UNS system
export interface IUnsPacket {
  // The message object of the packet
  message: IUnsExtendedMessage;
  
  // The HMAC signature of the message
  messageSignature?: string;

  // Automatically calculated interval between two packets in ms
  interval?: number;

  // Current library version
  readonly version: string;

  // Autogenerated sequence number
  sequenceId?: number;
}

export interface IUnsPackatParameters {

}

export interface IUnsMessage {
    command?: IUnsCommand;
    data?: IUnsData;
    event?: IUnsEvent;
    table?: IUnsTable;
    expiresAt?: ISO8601;
    createdAt?: ISO8601;
}

export interface IUnsExtendedMessage extends IUnsMessage {
  data?: IUnsExtendedData;
}

export interface ITopicObject {
  timestamp: string;
  attribute:string;
  attributeType: UnsAttributeType;
  topic:string;
  description:string;
  dataGroup:string;
  tags:string[] | null;
  attributeNeedsPersistence: boolean | null;
}

// API Interfaces below
export interface IApiObject {
  timestamp: string;
  attribute:string;
  topic:string;
  attributeType: UnsAttributeType;
  apiDescription?: string; // Optional description for the API endpoint
  apiHost: string; // Hostname of the service
  apiEndpoint: string; // API endpoint for virtual topics
  apiSwaggerEndpoint: string; // Swagger endpoint for API documentation
  apiMethod: "GET" | "POST" | "PUT" | "DELETE"; // HTTP method for API endpoint
  apiQueryParams: QueryParamDef[]; // query parameters for the API endpoint
}

export interface QueryParamDef {
  name: string;
  type: "string" | "number" | "boolean";
  required?: boolean;
  description?: string;
}

export interface IApiProxyOptions {
  jwtSecret?: string;
  jwks?: {
    wellKnownJwksUrl: string;
    activeKidUrl?: string;
    cacheTtlMs?: number;
    algorithms?: ("RS256" | "RS384" | "RS512")[];
  };
}

export interface IGetEndpointOptions {
  apiDescription?: string;
  tags?: string[];
  queryParams?: QueryParamDef[];
}
